import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Archive,
  ArchiveRestore,
  Briefcase,
  CheckCircle2,
  Eye,
  FlaskConical,
  Loader2,
  LogIn,
  MoreHorizontal,
  PauseCircle,
  ShieldCheck,
  UserX,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { ResponsiveTable } from "@/components/ResponsiveTable";
import { Pagination } from "@/components/Pagination";
import { PageHeader } from "@/components/PageHeader";
import { MetricCard, MetricGrid } from "@/components/MetricCard";
import { FilterPills } from "@/components/FilterPills";
import { StatusChip } from "@/components/StatusChip";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/datetime";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";

const STATUS_TABS = [
  { key: "active", label: "Active" },
  { key: "pending", label: "Pending" },
  { key: "rejected", label: "Rejected" },
  { key: "suspended", label: "Suspended" },
  { key: "archived", label: "Archived" },
  { key: "total", label: "Total" },
];

const ACTIONS_FOR = {
  pending: ["approve", "reject", "suspend", "archive"],
  active: ["suspend", "archive"],
  rejected: ["reactivate", "archive"],
  suspended: ["reactivate", "archive"],
  archived: ["unarchive"],
};

const ACTION_META = {
  approve:    { label: "Approve", icon: CheckCircle2, danger: false, requiresReason: false },
  reject:     { label: "Reject", icon: UserX, danger: true, requiresReason: true },
  suspend:    { label: "Suspend", icon: PauseCircle, danger: true, requiresReason: true },
  reactivate: { label: "Reactivate", icon: ShieldCheck, danger: false, requiresReason: false },
  archive:    { label: "Archive", icon: Archive, danger: false, requiresReason: false },
  unarchive:  { label: "Unarchive", icon: ArchiveRestore, danger: false, requiresReason: false },
};

export default function AdminCustomersPage() {
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PER_PAGE = 50;
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  const [emulatingId, setEmulatingId] = useState(null);
  const [emulateConfirm, setEmulateConfirm] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null);
  const [statusReason, setStatusReason] = useState("");
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const { user, emulate } = useAuth();
  const navigate = useNavigate();

  const loadAll = async (opts = {}) => {
    const wantPage = opts.page ?? page;
    const wantTab = opts.tab ?? activeTab;
    try {
      const params = new URLSearchParams({
        page: String(wantPage),
        per_page: String(PER_PAGE),
      });
      if (wantTab && wantTab !== "total") params.set("status", wantTab);
      const [s, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get(`/admin/customers?${params.toString()}`),
      ]);
      setStats(s.data);
      setCustomers(c.data.items || []);
      setTotal(c.data.total || 0);
      setPage(c.data.page || 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts = stats?.customer_status_counts || {};

  const filteredRows = useMemo(() => {
    if (activeTab === "total") {
      return customers.filter((u) => u.global_role !== "super_admin");
    }
    return customers;
  }, [customers, activeTab]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setLoading(true);
    loadAll({ tab: key, page: 1 });
  };
  const handlePageChange = (p) => {
    setLoading(true);
    loadAll({ page: p });
  };

  const handleConfirmEmulate = async () => {
    const target = emulateConfirm;
    if (!target) return;
    setEmulateConfirm(null);
    setEmulatingId(target.id);
    try {
      await emulate(target.id);
      toast.success(`Now viewing as ${target.email}`);
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e));
      setEmulatingId(null);
    }
  };

  const openStatusDialog = (u, action) => {
    setStatusReason("");
    setStatusDialog({ user: u, action });
  };

  const submitStatusChange = async () => {
    if (!statusDialog) return;
    const { user: target, action } = statusDialog;
    const meta = ACTION_META[action];
    const reason = statusReason.trim();
    if (meta.requiresReason && !reason) {
      toast.error("Please provide a reason");
      return;
    }
    setStatusSubmitting(true);
    try {
      await api.post(`/admin/customers/${target.id}/status`, {
        action,
        reason: meta.requiresReason ? reason : undefined,
      });
      toast.success(`${target.email} → ${action}`);
      setStatusDialog(null);
      setStatusReason("");
      await loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setStatusSubmitting(false);
    }
  };

  const columns = [
    {
      key: "email",
      label: "Email",
      mobileVariant: "title",
      render: (u) => (
        <div className="flex flex-col">
          <span className="rt-primary break-all">{u.name || u.email.split("@")[0]}</span>
          <span className="text-xs text-[#64748B] break-all">{u.email}</span>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (u) => <StatusChip status={u.status || "active"} />,
    },
    {
      key: "workspaces",
      label: "Workspaces",
      render: (u) => <span className="text-[#334155]">{u.workspace_count}</span>,
    },
    {
      key: "joined",
      label: "Joined",
      render: (u) => (
        <span className="text-[#64748B] text-xs">{formatDate(u.created_at)}</span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      mobileVariant: "actions",
      render: (u) => {
        const isSelf = u.id === user?.id;
        const isSuperAdmin = u.global_role === "super_admin";
        const status = u.status || "active";
        const allowed = isSuperAdmin || isSelf ? [] : (ACTIONS_FOR[status] || []);
        return (
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setEmulateConfirm(u)}
              disabled={isSelf || isSuperAdmin || emulatingId === u.id}
              data-testid={`emulate-button-${u.id}`}
              title={
                isSelf
                  ? "This is your own account"
                  : isSuperAdmin
                    ? "Cannot emulate another super admin"
                    : "Sign in as this user"
              }
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md border border-[#E5E7EB] bg-white text-sm font-medium text-[#0F172A] hover:border-[#CBD5E1] hover:bg-[#F8FAFC] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {emulatingId === u.id ? <Loader2 size={13} className="animate-spin" /> : <LogIn size={13} />}
              Sign in as user
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  disabled={isSuperAdmin || isSelf || allowed.length === 0}
                  data-testid={`row-actions-button-${u.id}`}
                  aria-label="Row actions"
                  className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] bg-white hover:bg-[#F8FAFC] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <MoreHorizontal size={14} className="text-[#64748B]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="rounded-lg border-[#E5E7EB] p-1 w-48"
                data-testid={`row-actions-menu-${u.id}`}
              >
                {allowed.map((a) => {
                  const m = ACTION_META[a];
                  const Icon = m.icon;
                  return (
                    <DropdownMenuItem
                      key={a}
                      onClick={() => openStatusDialog(u, a)}
                      data-testid={`row-action-${a}-${u.id}`}
                      className={`rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9] ${m.danger ? "text-[#B91C1C] focus:text-[#B91C1C]" : ""}`}
                    >
                      <Icon size={13} className="mr-2" />
                      {m.label}
                    </DropdownMenuItem>
                  );
                })}
                {allowed.length === 0 && (
                  <DropdownMenuItem disabled className="rounded-md px-2 py-2 text-sm text-[#94A3B8]">
                    No actions available
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const dialogMeta = statusDialog ? ACTION_META[statusDialog.action] : null;
  const dialogIsArchive = statusDialog?.action === "archive" || statusDialog?.action === "unarchive";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="admin-customers-page">
      <PageHeader
        title="Customers"
        description="Manage customer accounts, workspaces and access."
      />

      <MetricGrid columns={4}>
        <MetricCard testId="stat-users" label="Total Users" icon={Users} value={stats?.users ?? "—"} />
        <MetricCard testId="stat-customers" label="Customers" icon={UserX} value={stats?.customers ?? "—"} />
        <MetricCard testId="stat-workspaces" label="Workspaces" icon={Briefcase} value={stats?.workspaces ?? "—"} />
        <MetricCard testId="stat-tests" label="Test Cases" icon={FlaskConical} value={stats?.test_cases ?? "—"} />
      </MetricGrid>

      <FilterPills
        testId="status-tab"
        tabs={STATUS_TABS}
        value={activeTab}
        onChange={handleTabChange}
        counts={counts}
      />

      {loading ? (
        <div className="surface-card p-6 text-sm text-[#64748B]">Loading…</div>
      ) : filteredRows.length === 0 ? (
        <div className="surface-card py-12 text-center text-[#64748B] text-sm">
          <Users size={20} className="mx-auto mb-2 text-[#94A3B8]" />
          No customers in <span className="font-semibold capitalize">{activeTab}</span> yet.
        </div>
      ) : (
        <>
          <ResponsiveTable
            testId="customers-table"
            columns={columns}
            rows={filteredRows}
            getRowTestId={(u) => `customer-row-${u.id}`}
          />
          <Pagination
            testId="customers-pagination"
            page={page}
            perPage={PER_PAGE}
            total={total}
            onChange={handlePageChange}
          />
        </>
      )}

      {/* Emulate confirm dialog */}
      <AlertDialog open={!!emulateConfirm} onOpenChange={(open) => !open && setEmulateConfirm(null)}>
        <AlertDialogContent
          data-testid="emulate-confirm-dialog"
          className="rounded-xl border border-[#E5E7EB] bg-white p-0 max-w-md gap-0"
        >
          <AlertDialogHeader className="px-6 pt-6 pb-3 space-y-3">
            <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-[#3B82F6]/10 text-[#3B82F6]">
              <Eye size={18} />
            </div>
            <p className="text-xs font-medium text-[#3B82F6]">Super Admin · Sign in as user</p>
            <AlertDialogTitle className="font-['Geist'] font-semibold tracking-tight text-2xl leading-tight text-[#0F172A]">
              Sign in as this customer?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#475569] leading-relaxed pt-1">
              You will see Probe exactly as{" "}
              <span
                className="font-mono text-[12px] text-[#0F172A] bg-[#F1F5F9] px-1.5 py-0.5 rounded break-all"
                data-testid="emulate-confirm-email"
              >
                {emulateConfirm?.email}
              </span>{" "}
              does. Every action will be tagged in the emulation audit log under your admin account.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="mx-6 my-4 border border-[#E5E7EB] bg-[#F8FAFC] rounded-lg p-3 space-y-1.5">
            <div className="flex items-start gap-2">
              <span className="text-xs text-[#64748B] mt-0.5 shrink-0 w-16">You</span>
              <span className="text-sm font-medium text-[#0F172A] break-all">{user?.email}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-xs text-[#64748B] mt-0.5 shrink-0 w-16">Target</span>
              <span className="text-sm font-medium text-[#0F172A] break-all">{emulateConfirm?.email}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-xs text-[#64748B] mt-0.5 shrink-0 w-16">Return</span>
              <span className="text-xs text-[#475569]">Click &quot;Return to admin&quot; in the top banner any time</span>
            </div>
          </div>

          <AlertDialogFooter className="px-6 pb-6 pt-2 flex-row justify-end gap-2 sm:gap-2">
            <AlertDialogCancel
              data-testid="emulate-confirm-cancel"
              className="rounded-md border border-[#E5E7EB] bg-white text-[#0F172A] text-sm font-medium h-9 px-4 mt-0 hover:bg-[#F8FAFC]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmEmulate}
              data-testid="emulate-confirm-accept"
              className="rounded-md bg-[#3B82F6] text-white text-sm font-medium h-9 px-4 hover:bg-[#2563EB] inline-flex items-center gap-1.5"
            >
              <LogIn size={13} />
              Sign in as user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status change dialog */}
      <AlertDialog
        open={!!statusDialog}
        onOpenChange={(open) => {
          if (!open && !statusSubmitting) {
            setStatusDialog(null);
            setStatusReason("");
          }
        }}
      >
        <AlertDialogContent
          data-testid="status-change-dialog"
          className="rounded-xl border border-[#E5E7EB] bg-white p-0 max-w-md gap-0"
        >
          <AlertDialogHeader className="px-6 pt-6 pb-3 space-y-3">
            <div
              className={`flex items-center justify-center w-11 h-11 rounded-lg ${
                dialogMeta?.danger ? "bg-[#EF4444]/10 text-[#B91C1C]" : "bg-[#3B82F6]/10 text-[#3B82F6]"
              }`}
            >
              {dialogMeta && <dialogMeta.icon size={18} />}
            </div>
            <p
              data-testid="status-change-dialog-eyebrow"
              className={`text-xs font-medium ${dialogMeta?.danger ? "text-[#B91C1C]" : "text-[#3B82F6]"}`}
            >
              Super Admin · {dialogMeta?.label || ""}
            </p>
            <AlertDialogTitle className="font-['Geist'] font-semibold tracking-tight text-2xl leading-tight text-[#0F172A]">
              {statusDialog?.action === "approve" && "Approve this account?"}
              {statusDialog?.action === "reject" && "Reject this account?"}
              {statusDialog?.action === "suspend" && "Suspend this account?"}
              {statusDialog?.action === "reactivate" && "Reactivate this account?"}
              {statusDialog?.action === "archive" && "Archive this account?"}
              {statusDialog?.action === "unarchive" && "Restore this account?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#475569] leading-relaxed pt-1">
              {statusDialog?.action === "approve" && "They'll be able to sign in immediately and access their workspace."}
              {statusDialog?.action === "reject" && "They'll be blocked from signing in. The reason is stored on the audit log."}
              {statusDialog?.action === "suspend" && "They'll be temporarily blocked from signing in until you reactivate them."}
              {statusDialog?.action === "reactivate" && "They'll be able to sign in immediately again."}
              {statusDialog?.action === "archive" && "They'll be hidden from the default tabs and blocked from signing in. You can restore them later from the Archived tab."}
              {statusDialog?.action === "unarchive" && "They'll be moved back to their pre-archive status."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="mx-6 my-4 border border-[#E5E7EB] bg-[#F8FAFC] rounded-lg p-3 space-y-1.5">
            <div className="flex items-start gap-2">
              <span className="text-xs text-[#64748B] mt-0.5 shrink-0 w-20">Customer</span>
              <span className="text-sm font-medium text-[#0F172A] break-all" data-testid="status-dialog-target-email">
                {statusDialog?.user?.email}
              </span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-xs text-[#64748B] mt-0.5 shrink-0 w-20">Current</span>
              <span className="text-sm font-medium text-[#0F172A] capitalize">
                {statusDialog?.user?.status || "active"}
              </span>
            </div>
            {dialogIsArchive && statusDialog?.action === "unarchive" && (
              <div className="flex items-start gap-2">
                <span className="text-xs text-[#64748B] mt-0.5 shrink-0 w-20">Restore to</span>
                <span className="text-sm font-medium text-[#0F172A] capitalize">
                  {statusDialog?.user?.archived_from_status || "active"}
                </span>
              </div>
            )}
          </div>

          {dialogMeta?.requiresReason && (
            <div className="px-6 pb-2 space-y-1.5">
              <label className="text-xs font-medium text-[#475569]" htmlFor="status-reason-input">
                Reason <span className="text-[#B91C1C]">*</span>
              </label>
              <Textarea
                id="status-reason-input"
                data-testid="status-reason-input"
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                placeholder="Visible only to admins. e.g. Payment overdue / Suspicious activity"
                className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6] min-h-[88px]"
                maxLength={500}
              />
              <p className="text-[11px] text-[#94A3B8] text-right">{statusReason.length}/500</p>
            </div>
          )}

          <AlertDialogFooter className="px-6 pb-6 pt-2 flex-row justify-end gap-2 sm:gap-2">
            <AlertDialogCancel
              disabled={statusSubmitting}
              data-testid="status-change-cancel"
              className="rounded-md border border-[#E5E7EB] bg-white text-[#0F172A] text-sm font-medium h-9 px-4 mt-0 hover:bg-[#F8FAFC]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                submitStatusChange();
              }}
              disabled={statusSubmitting || (dialogMeta?.requiresReason && !statusReason.trim())}
              data-testid="status-change-accept"
              className={`rounded-md text-sm font-medium h-9 px-4 inline-flex items-center gap-1.5 ${
                dialogMeta?.danger
                  ? "bg-[#EF4444] text-white hover:bg-[#DC2626]"
                  : "bg-[#3B82F6] text-white hover:bg-[#2563EB]"
              }`}
            >
              {statusSubmitting ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                dialogMeta && <dialogMeta.icon size={13} />
              )}
              {dialogMeta?.label}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
